import compose from 'ramda/src/compose';
import equals from 'ramda/src/equals';
import anyPass from 'ramda/src/anyPass';
import uniq from 'ramda/src/uniq';
import find from 'ramda/src/find';
import filter from 'ramda/src/filter';
import lensIndex from 'ramda/src/lensIndex';
import merge from 'ramda/src/merge';
import map from 'ramda/src/map';
import assoc from 'ramda/src/assoc';
import converge from 'ramda/src/converge';
import mergeAll from 'ramda/src/mergeAll';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import propOr from 'ramda/src/propOr';
import flatten from 'ramda/src/flatten';
import reduceBy from 'ramda/src/reduceBy';
import values from 'ramda/src/values';
import not from 'ramda/src/not';
import groupBy from 'ramda/src/groupBy';
import view from 'ramda/src/view';
import any from 'ramda/src/any';
import isEmptyOrNil from '@util/fn/isEmptyOrNil';
import {toRmaLineItemInput} from '@services/SmsService/mutations';

/**
 * check if any RmaLines still have not been received by checking for unitStatus of WAIT RCV
 */
const isCanceled = propEq( 'unitStatus', 'CANCELED' );
const isPending = propEq( 'unitStatus', 'PENDING' );
const isWaitingToReceive = propEq( 'unitStatus', 'WAIT RCV' );
export const hasUnreceivedItems = any( isWaitingToReceive );
/**
 * If any of the items unitStatus is not equal to 'PENDING' or 'WAIT RCV' then assume we've received something
 */
export const hasReceivedSomeItems = compose(
    any( not ),
    map( anyPass( [isWaitingToReceive, isPending, isCanceled] ) )
);

export const VENDOR_LABELS = {
    UPI: 'Ultradent Products, Inc.',
    AZENA: 'Azena Medical',
    ACTEON: 'ACTEON',
    OSSEO: 'Osseo'
};

const UNIT_STATUS_MAP = {
    'CANCELED': {description: 'Canceled', sequence: 0},
    'PENDING': {description: 'Pending', sequence: 0},
    'WAIT RCV': {description: 'Waiting to receive', sequence: 0.66},
    'PRT RCV': {description: 'Equipment received', sequence: 1.0},
    'QUOTE': {description: 'Pending quote', sequence: 1.0},
    'QUOTESUBMITTED': {description: 'Repairs quoted', sequence: 1.66},
    'DECLINE CONVERT': {description: 'Customer declined repair', sequence: 1.66},
    'DECLINE RETURN': {description: 'Customer declined repair', sequence: 1.66},
    'DECLINE SCRAP': {description: 'Customer declined repair', sequence: 1.66},
    'WAIT CUST': {description: 'Awaiting customer approval', sequence: 2.0},
    'APPROVED': {description: 'Approved & ready to repair', sequence: 2.66},
    'RPRINVEST': {description: 'Approved & ready to repair', sequence: 2.66},
    'REPAIR': {description: 'Perform repairs', sequence: 3.0},
    'WIP': {description: 'Repair in progress', sequence: 3.66},
    '3RD': {description: 'Repair in progress', sequence: 3.66},
    'RPRCOMPLETE': {description: 'Repairs complete', sequence: 3.9},
    'RETURN': {description: 'Return equipment', sequence: 4.0},
    'RDY': {description: 'Ready to return', sequence: 4.0},
    'RDYUNREPAIRED': {description: 'Ready to return', sequence: 4.0},
    'RETURNING': {description: 'Returning equipment', sequence: 4.7},
    'SHIP': {description: 'Equipment returned', sequence: 5.0}
};

export function getUnitStatuses ( status ) {
    /**
     *  1. Waiting to receive / Equipment received
     *  2. Pending quote / Repairs quoted
     *  3. Awaiting quote approval / Quote approved, ready to repair
     *  4. Repair in progress / Repairs complete
     *  5. Ready to return
     *  6. Equipment returned
     */
    console.log({status});
    const statuses = UNIT_STATUS_MAP;
    const {description, sequence} = statuses[status] || statuses['PENDING'];
    const steps = [
        statuses['WAIT RCV'],
        statuses['QUOTE'],
        statuses['WAIT CUST'],
        statuses['REPAIR'],
        statuses['RETURN']
    ];

    if ( sequence >= 1 ) {
        steps.splice( 0, 1, statuses['PRT RCV'] );
    }
    if ( sequence > 1 ) {
        steps.splice( 1, 1, statuses['QUOTESUBMITTED'] );
    }
    if ( sequence > 2 ) {
        steps.splice( 2, 1, statuses['APPROVED'] );
    }
    if ( sequence > 3 ) {
        steps.splice( 3, 1, statuses['WIP'] );
    }
    if ( sequence > 3.75 ) {
        steps.splice( 3, 1, statuses['RPRCOMPLETE'] );
    }
    if ( sequence > 4 ) {
        steps.splice( 4, 1, statuses['RETURNING'] );
    }
    if ( sequence >= 5 ) {
        steps.splice( 4, 1, statuses['SHIP'] );
    }

    return {
        steps,
        description,
        sequence
    };
}

export function getRmaByRequestId ( id, list ) {
    return list.find( ( {requestId} ) => requestId === id );
}

export function getEquipmentById ( id, list ) {
    const bySerialId = ( {serialId} ) => idList.includes( serialId );
    const idList = Array.isArray( id ) ? id : [id];
    return compose( uniq, filter( bySerialId ) )( list );
}

export function getAddressByPlaceId ( placeId, list ) {
    return find( propEq( 'placeId', placeId ) )( list );
}

export function getCartLineData ( cart, customerEquipment ) {
    const ids = cart.map( ( {serialId} ) => serialId );
    const equipList = getEquipmentById( ids, customerEquipment );

    // merge CartLines with equipmentList
    // fixme - bug occurs when old item in cart no longer exists in EquipmentList, empty cart item displays
    //  need a strategy for cleaning up old items in cart
    // todo -> could be optimized
    return cart.map( line => {
        const equipItem = equipList.find( ( {serialId} ) => serialId === line.serialId );
        return merge( equipItem, line );
    } );
}

export function getReturnByDate ( createdDate ) {
    const d = new Date( createdDate );
    // set expiration to 30 days from now
    d.setHours( 24 * 30 );
    return d.toLocaleDateString();
}

const assocRmaWithLines = ( requestId, rmaLineItems ) => map( assoc( 'requestId', requestId ) )( rmaLineItems );
// pluck 'requestId' field from RMA item and associate it with each rmaLineItem on RMA
const applyRequestIdToLine = converge( assocRmaWithLines, [prop( 'requestId' ), propOr( [], 'rmaLineItems' )] );

// Rma => Boolean
// export const isRmaOpen = compose( equals( 'OPEN' ), propOr( null, 'requestStatus' ) );
export const isRmaOpen = rmaLine => /OPEN/.test( rmaLine.requestStatus );
// [Rma] => [RmaLines]
export const getAllRmaLines = compose( flatten, map( applyRequestIdToLine ) );
// [Rma] => [RmaLines]
export const getOpenRmaLines = compose( getAllRmaLines, filter( isRmaOpen ) );
export const getCanceledRmaLines = compose(filter( item => item.requestLineStatus === 'CANCELED' ),  getAllRmaLines );

export function getLineItemById ( id, list ) {
    if ( !list || !list.length ) {
        return null;
    }
    return list.find( ( {serialId} ) => serialId === id );
}

/* Group RMA shipments by vendorKey = placeId + vendor */
const toShipmentKey = x => x.vendor + ':' + x.placeId;
export const groupByVendorAndPlaceId = reduceBy( ( a, b ) => a.concat( b ), [], toShipmentKey );

/**
 * [cartLineItem] => {included: [cartLineItem], excluded: [cartLineItem] }
 * If the item has an rmaDetail field then it's already assoc with open rma and should be dumped in "excluded" bucket
 */
export const groupLineItemsByAvailability = groupBy( ( {rmaDetail} ) => (rmaDetail != null) ? 'excluded' : 'included' );

/* RMA Form submission helpers */
function mapLineDescriptions ( line ) {
    return line.equipmentIssue ?
        `Serial #: ${line.serialId}
        ${line.equipmentIssue}`
        : null
}

const getLineDescriptions = compose(
    filter( compose( not, isEmptyOrNil ) ),
    map( mapLineDescriptions )
);

function toRmaRequest ( {groupId, contactInfo, lineItems} ) {
    const {placeId, address, vendor} = lineItems[0];
    return {
        groupId,
        vendor,
        placeId,
        addressId: address.id,
        description: getLineDescriptions( lineItems ).join( '\n\r' ),
        ...contactInfo,
        rmaLineItems: lineItems.map( toRmaLineItemInput )
    };
}

export function toContactInfo ( data ) {
    const name = data.contactFullName.split( ' ' );
    return {
        firstName: name[0],
        lastName: name[1],
        email: data.email,
        phone: data.phone.replace( /[^0-9]/gi, '' ) // return numbers only
    };
}

export function mapShipmentGroupsToRma ( groupedShipments, contactInfo ) {
    return Object
        .entries( groupedShipments )
        .map( ( [key, lineItems] ) =>
            toRmaRequest( {
                groupId: key,
                contactInfo,
                lineItems
            } )
        );
}

const propFromHead = ( propName, list ) => compose( prop( propName ), view( lensIndex( 0 ) ) )( list );

/**
 *
 * @param groupedShipments [[EquipmentItem]] - Array matrix of EquipmentItems grouped by placeId
 * @param contactInfo Object - Customer info firstNAme, lastName, email, phone
 * @param addresses Object - GraphQL Address
 * @param rmaResponse - GraphQL createRmaRequest payload
 * @returns Array - Array contains the original rmaResponse with the original request data stitched in along with address and vendor
 */
export function stitchRmaRequestToResponse ( {groupedShipments, contactInfo, addresses, rmaResponse} ) {
    const rmaResponseById = groupBy( prop( 'groupId' ), rmaResponse );
    // map over each shipmentGroup, pluck the placeId and use to map to rmaResponse for merging
    return Object
        .entries( groupedShipments )
        .map( ( [key, lineItems] ) => {
            const placeId = propFromHead( 'placeId', lineItems );
            const vendor = propFromHead( 'vendor', lineItems );
            // stitch response to shipment group by index set as "groupId"
            const rmaDetail = view( lensIndex( 0 ), rmaResponseById[key] );
            const address = getAddressByPlaceId( placeId, addresses ) || {};

            return mergeAll( [
                contactInfo,
                {vendor},
                {address},
                {rmaLineItems: lineItems},
                rmaDetail
            ] );
        } );
}
